import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Play, CheckCircle2, Clock, ArrowRight, ChevronDown, ChevronUp,
  UserPlus, Globe, Settings, Rocket, BarChart3, Bot, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TutorialsPage = () => {
  const [expandedTutorial, setExpandedTutorial] = useState(null);

  // HowTo Schema for the main getting started tutorial
  const mainHowToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Set Up AutomateSEO.ai for Your Business",
    "description": "Complete step-by-step guide to setting up automated SEO, content generation, and social media management for your business.",
    "image": "https://automateseo.ai/tutorial-hero.jpg",
    "totalTime": "PT15M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": "0"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Website URL"
      },
      {
        "@type": "HowToSupply",
        "name": "Google Search Console Access"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "AutomateSEO.ai Account"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Create Your Account",
        "text": "Sign up for AutomateSEO.ai using your email. Activation takes less than 2 minutes.",
        "url": "https://automateseo.ai/register",
        "image": "https://automateseo.ai/step1-signup.jpg"
      },
      {
        "@type": "HowToStep",
        "name": "Complete Onboarding",
        "text": "Enter your business details, website URL, target keywords, and social media accounts in our guided wizard.",
        "image": "https://automateseo.ai/step2-onboarding.jpg"
      },
      {
        "@type": "HowToStep",
        "name": "Connect Google Search Console",
        "text": "Link your Google Search Console to enable real-time SEO monitoring and automated indexing.",
        "image": "https://automateseo.ai/step3-gsc.jpg"
      },
      {
        "@type": "HowToStep",
        "name": "Configure AI Settings",
        "text": "Set your content preferences, posting schedule, and automation frequency.",
        "image": "https://automateseo.ai/step4-settings.jpg"
      },
      {
        "@type": "HowToStep",
        "name": "Launch Automation",
        "text": "Activate the AI agents and watch as they optimize your SEO, generate content, and manage social media automatically.",
        "image": "https://automateseo.ai/step5-launch.jpg"
      }
    ]
  };

  // Additional HowTo schemas for other tutorials
  const tutorials = [
    {
      id: 'getting-started',
      title: "Getting Started with AutomateSEO.ai",
      description: "Learn how to set up your account and configure AI automation in 15 minutes",
      duration: "15 min",
      difficulty: "Beginner",
      icon: Rocket,
      steps: [
        {
          number: 1,
          title: "Create Your Account",
          description: "Sign up using email or Google. It takes less than 2 minutes.",
          details: [
            "Go to automateseo.ai/register",
            "Enter your business email and create a password",
            "Verify your email address",
            "Complete your profile with business name"
          ]
        },
        {
          number: 2,
          title: "Start Onboarding",
          description: "Our guided wizard collects essential information about your business.",
          details: [
            "Enter your website URL",
            "Specify your business category",
            "Add target keywords (up to 10)",
            "Connect social media accounts"
          ]
        },
        {
          number: 3,
          title: "Connect Google Search Console",
          description: "Link your GSC for real-time data and automated indexing.",
          details: [
            "Click 'Connect Google Search Console'",
            "Authorize AutomateSEO.ai access",
            "Select your property from the list",
            "Verify connection is active"
          ]
        },
        {
          number: 4,
          title: "Configure Automation",
          description: "Set your preferences for content and posting frequency.",
          details: [
            "Choose content types (blog, social, video)",
            "Set posting schedule (daily, weekly)",
            "Configure brand voice and tone",
            "Select target platforms"
          ]
        },
        {
          number: 5,
          title: "Launch & Monitor",
          description: "Activate AI agents and track performance on your dashboard.",
          details: [
            "Click 'Activate Automation'",
            "Review AI-generated content before publishing",
            "Monitor rankings on the SEO dashboard",
            "Receive weekly performance reports"
          ]
        }
      ],
      howToSchema: mainHowToSchema
    },
    {
      id: 'content-generation',
      title: "AI Content Generation Tutorial",
      description: "Master AI-powered content creation for blogs, social media, and more",
      duration: "10 min",
      difficulty: "Beginner",
      icon: Bot,
      steps: [
        {
          number: 1,
          title: "Access Content Generator",
          description: "Navigate to the AI Content section in your dashboard.",
          details: [
            "Log into your AutomateSEO.ai account",
            "Click 'AI Content' in the sidebar",
            "Select content type (blog, social, email)"
          ]
        },
        {
          number: 2,
          title: "Configure Generation Settings",
          description: "Set parameters for your content.",
          details: [
            "Enter your topic or keywords",
            "Choose tone (professional, casual, persuasive)",
            "Set content length",
            "Add any specific instructions"
          ]
        },
        {
          number: 3,
          title: "Generate & Review",
          description: "Let AI create content and review the output.",
          details: [
            "Click 'Generate Content'",
            "Wait 10-30 seconds for completion",
            "Review generated content",
            "Make edits if needed"
          ]
        },
        {
          number: 4,
          title: "Publish or Schedule",
          description: "Post immediately or schedule for later.",
          details: [
            "Click 'Publish Now' for immediate posting",
            "Or click 'Schedule' to set date/time",
            "Content is auto-optimized for SEO",
            "Track performance in analytics"
          ]
        }
      ]
    },
    {
      id: 'seo-monitoring',
      title: "SEO Dashboard & Monitoring",
      description: "Understand your SEO metrics and track ranking improvements",
      duration: "8 min",
      difficulty: "Intermediate",
      icon: BarChart3,
      steps: [
        {
          number: 1,
          title: "Access SEO Dashboard",
          description: "Navigate to your SEO monitoring center.",
          details: [
            "Click 'SEO Dashboard' in navigation",
            "View overall SEO score",
            "Check ranking changes",
            "Review technical issues"
          ]
        },
        {
          number: 2,
          title: "Understand Your Score",
          description: "Learn what each metric means.",
          details: [
            "SEO Score: Overall site health (0-100)",
            "AEO Score: Answer engine readiness",
            "GEO Score: AI citation potential",
            "Technical Score: Site performance"
          ]
        },
        {
          number: 3,
          title: "Review Rankings",
          description: "Track your keyword positions.",
          details: [
            "View top ranking keywords",
            "See position changes over time",
            "Identify opportunities",
            "Track competitor rankings"
          ]
        },
        {
          number: 4,
          title: "Download Reports",
          description: "Export detailed PDF reports.",
          details: [
            "Click 'Download Report'",
            "Choose report type",
            "PDF includes all metrics",
            "Share with team or clients"
          ]
        }
      ]
    },
    {
      id: 'social-automation',
      title: "Social Media Automation Setup",
      description: "Connect and automate your social media presence across platforms",
      duration: "12 min",
      difficulty: "Beginner",
      icon: Globe,
      steps: [
        {
          number: 1,
          title: "Connect Social Accounts",
          description: "Link your social media profiles.",
          details: [
            "Go to Settings > Integrations",
            "Click 'Connect' for each platform",
            "Authorize AutomateSEO.ai",
            "Supported: Twitter, Facebook, Instagram"
          ]
        },
        {
          number: 2,
          title: "Create Content Calendar",
          description: "Set up your posting schedule.",
          details: [
            "Navigate to Social Media Manager",
            "Click 'Create Calendar'",
            "Set posting frequency per platform",
            "Choose best posting times"
          ]
        },
        {
          number: 3,
          title: "Generate Social Content",
          description: "Use AI to create engaging posts.",
          details: [
            "Click 'Generate Posts'",
            "AI creates week's worth of content",
            "Review and approve posts",
            "Edit captions and hashtags"
          ]
        },
        {
          number: 4,
          title: "Enable Auto-Posting",
          description: "Let AI publish on schedule.",
          details: [
            "Toggle 'Auto-Post' for each platform",
            "Set approval workflow (auto or manual)",
            "AI posts at optimal times",
            "Track engagement in dashboard"
          ]
        }
      ]
    }
  ];

  const toggleTutorial = (id) => {
    setExpandedTutorial(expandedTutorial === id ? null : id);
  };

  return (
    <>
      <Helmet>
        <title>Tutorials & How-To Guides | AutomateSEO.ai</title>
        <meta name="description" content="Step-by-step tutorials for setting up AI-powered SEO automation, content generation, and social media management with AutomateSEO.ai." />
        <meta name="keywords" content="SEO tutorial, AI content generation guide, social media automation tutorial, AutomateSEO guide" />
        <link rel="canonical" href="https://automateseo.ai/tutorials" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(mainHowToSchema)}
        </script>
        
        {/* ItemList Schema for tutorial collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AutomateSEO.ai Tutorials",
            "description": "Complete collection of tutorials for mastering SEO automation",
            "itemListElement": tutorials.map((tutorial, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": tutorial.title,
              "url": `https://automateseo.ai/tutorials#${tutorial.id}`
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Navigation */}
        <nav className="fixed w-full z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="/AutomateSEO-logo.png" 
                  alt="AutomateSEO.ai" 
                  className="h-10 w-auto"
                />
                <span className="text-xl font-bold text-white">AutomateSEO.ai</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/seo-guide">
                  <Button variant="ghost" className="text-slate-300 hover:text-white">
                    SEO Guide
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-24 pb-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
              <Play className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">Step-by-Step Tutorials</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Learn How to <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Automate Everything</span>
            </h1>
            
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Follow our step-by-step guides to master AI-powered SEO automation, 
              content generation, and social media management.
            </p>
          </div>
        </section>

        {/* Tutorials List */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto space-y-6">
            {tutorials.map((tutorial) => (
              <div 
                key={tutorial.id}
                id={tutorial.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden"
              >
                {/* Tutorial Header */}
                <button
                  onClick={() => toggleTutorial(tutorial.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                      <tutorial.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-bold text-white">{tutorial.title}</h2>
                      <p className="text-slate-400">{tutorial.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="hidden md:flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-slate-400">
                        <Clock className="w-4 h-4" />
                        {tutorial.duration}
                      </span>
                      <span className="px-3 py-1 bg-slate-700 rounded-full text-slate-300">
                        {tutorial.difficulty}
                      </span>
                    </div>
                    {expandedTutorial === tutorial.id ? (
                      <ChevronUp className="w-6 h-6 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Steps */}
                {expandedTutorial === tutorial.id && (
                  <div className="border-t border-slate-700/50 p-6">
                    <div className="space-y-6">
                      {tutorial.steps.map((step, index) => (
                        <div 
                          key={step.number}
                          className="flex gap-4"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                              {step.number}
                            </div>
                            {index < tutorial.steps.length - 1 && (
                              <div className="w-0.5 h-full bg-slate-700 mx-auto mt-2" />
                            )}
                          </div>
                          <div className="flex-grow pb-6">
                            <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                            <p className="text-slate-400 mb-4">{step.description}</p>
                            <ul className="space-y-2">
                              {step.details.map((detail, detailIndex) => (
                                <li key={detailIndex} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-slate-300">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-slate-700/50 flex justify-center">
                      <Link to="/register">
                        <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                          Try It Now <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-3xl p-8 md:p-12">
              <Zap className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start?
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Put these tutorials into practice with a free 3-day trial.
                No credit card required.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-700/50 py-8 px-4">
          <div className="max-w-7xl mx-auto text-center text-slate-400">
            <p>© 2025 AutomateSEO.ai. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TutorialsPage;
